import React, { useRef, useEffect, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import { useAnda } from './AndaContext';
import { submitFeedback } from '../../api/andaApi';
import toastAlert from '../../js/Base/ToastAlert';
import './anda.scss';

const DEFAULT_GREETING = '您好，我是安答，請問需要對哪項問題進行協助？或是其他有關雲端資安健診的問題？';

export default function AndaMessageList() {
  const { messages, streamingContent, streamingImages, isStreaming, threadId, streamingThreadId } = useAnda();
  const showStreamingHere = isStreaming && (threadId === streamingThreadId || (streamingThreadId == null && threadId == null));
  const scrollRef = useRef(null);
  const acctNo = Number(localStorage.getItem('user_id')) || null;
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});

  const handleCopy = useCallback(async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toastAlert.autoClose(true).showSuccess('已複製到剪貼簿');
    } catch (err) {
      toastAlert.autoClose(true).showError('複製失敗');
    }
  }, []);

  const handleFeedback = useCallback(async (messageId, rating) => {
    if (!threadId || !acctNo || !messageId) {
      toastAlert.autoClose(true).showError('無法提交回饋');
      return;
    }
    try {
      await submitFeedback({ thread_id: threadId, acct_no: acctNo, message_id: messageId, rating });
      setFeedbackByMessageId((prev) => ({ ...prev, [messageId]: rating }));
      toastAlert.autoClose(true).showSuccess(rating === 1 ? '感謝您的正面回饋' : '感謝您的回饋');
    } catch (err) {
      toastAlert.autoClose(true).showError(err.response?.data?.error || '提交失敗');
    }
  }, [threadId, acctNo]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent, streamingImages, showStreamingHere]);

  const displayMessages = messages.length === 0 && !showStreamingHere
    ? [{ role: 'assistant', content: DEFAULT_GREETING }]
    : messages;

  const renderContent = (content) => {
    if (Array.isArray(content)) {
      return content.map((block, i) => {
        if (block.type === 'text') {
          return <ReactMarkdown key={i} remarkPlugins={[remarkGfm]}>{block.content || ''}</ReactMarkdown>;
        }
        if (block.type === 'image' && block.data) {
          const src = `data:${block.mime_type || 'image/png'};base64,${block.data}`;
          return <img key={i} src={src} alt="" className="anda-message-image" />;
        }
        return null;
      });
    }
    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>;
  };

  const getTextForCopy = (content) => {
    if (Array.isArray(content)) {
      return content.filter((b) => b.type === 'text').map((b) => b.content).join('\n\n');
    }
    return content;
  };

  return (
    <div className="anda-message-list" ref={scrollRef}>
      {displayMessages.map((msg, idx) => (
        <div
          key={msg.id || idx}
          className={`anda-message anda-message-${msg.role}`}
        >
          <div className={`anda-message-bubble ${msg.role === 'assistant' ? 'anda-markdown' : ''}`}>
            {msg.role === 'assistant' ? (
              <>
                <div className="anda-message-content">
                  {renderContent(msg.content)}
                </div>
                <div className="anda-message-actions">
                  <Tooltip title="複製" placement="top">
                    <IconButton size="small" onClick={() => handleCopy(getTextForCopy(msg.content))} aria-label="複製" sx={{ color: '#666', padding: '4px' }}>
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {msg.id && threadId && (
                    <>
                      <Tooltip title="有幫助" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleFeedback(msg.id, 1)}
                          aria-label="有幫助"
                          sx={{ color: feedbackByMessageId[msg.id] === 1 ? '#7FBE25' : '#666', padding: '4px' }}
                        >
                          <ThumbUp sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="沒幫助" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleFeedback(msg.id, -1)}
                          aria-label="沒幫助"
                          sx={{ color: feedbackByMessageId[msg.id] === -1 ? '#d32f2f' : '#666', padding: '4px' }}
                        >
                          <ThumbDown sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </>
            ) : (
              msg.content
            )}
          </div>
        </div>
      ))}
      {showStreamingHere && !streamingContent && (
        <div className="anda-message anda-message-assistant">
          <div className="anda-message-bubble anda-message-loading">
            <span className="anda-spinner" aria-label="載入中" />
            <span>思考中...</span>
          </div>
        </div>
      )}
      {showStreamingHere && (streamingContent || streamingImages.length > 0) && (
        <div className="anda-message anda-message-assistant">
          <div className="anda-message-bubble anda-markdown">
            <div className="anda-message-content">
              {streamingContent && (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                  <span className="anda-cursor" />
                </>
              )}
              {streamingImages.map((img, i) => (
                <img key={i} src={`data:${img.mime_type || 'image/png'};base64,${img.data}`} alt="" className="anda-message-image" />
              ))}
            </div>
            <div className="anda-message-actions">
              <Tooltip title="複製" placement="top">
                <IconButton size="small" onClick={() => handleCopy(streamingContent)} aria-label="複製" sx={{ color: '#666', padding: '4px' }}>
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
